import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card, Table, Button, Input, Tag, Space, Popconfirm, notification,
  Select, Modal, Form, ColorPicker, InputNumber, Divider,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  FileImageOutlined, SettingOutlined, SaveOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { Search } = Input;

const token = () => localStorage.getItem('accessToken');
const authH = () => ({ Authorization: `Bearer ${token()}` });

// ─── Category Manager Modal ───────────────────────────────────────────────────
const COLOR_PRESETS = [
  '#7a0000','#1d4ed8','#7c3aed','#0891b2','#059669',
  '#d97706','#dc2626','#6b7280','#374151','#92400e',
];

function CategoryManager({ open, onClose, onChanged }) {
  const [cats,    setCats]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [form]                = Form.useForm();
  const [saving,  setSaving]  = useState(false);
  const [editId,  setEditId]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/portal-categories`, { headers: authH() });
      if (r.data.status) setCats(r.data.result);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { load(); form.resetFields(); setEditId(null); } }, [open, load, form]);

  const save = async (values) => {
    setSaving(true);
    try {
      const payload = { name: values.name, color: values.color || '#7a0000', sort_order: values.sort_order || 0 };
      if (editId) {
        await axios.patch(`${baseUrl}/portal-categories/${editId}`, payload, { headers: authH() });
        notification.success({ message: 'Category updated' });
      } else {
        await axios.post(`${baseUrl}/portal-categories`, payload, { headers: authH() });
        notification.success({ message: 'Category created' });
      }
      form.resetFields(); setEditId(null);
      load(); onChanged();
    } catch (err) {
      notification.error({ message: 'Save failed', description: err.response?.data?.message });
    } finally { setSaving(false); }
  };

  const startEdit = (cat) => {
    setEditId(cat.id);
    form.setFieldsValue({ name: cat.name, color: cat.color, sort_order: cat.sort_order });
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${baseUrl}/portal-categories/${id}`, { headers: authH() });
      notification.success({ message: 'Category disabled' });
      load(); onChanged();
    } catch { notification.error({ message: 'Delete failed' }); }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={520} title="Category Management">
      <Form form={form} layout="inline" onFinish={save} style={{ marginBottom: 16 }}>
        <Form.Item name="name" rules={[{ required: true, message: 'Name required' }]} style={{ flex: 1 }}>
          <Input placeholder="Category name (Hindi or English)" />
        </Form.Item>
        <Form.Item name="color" initialValue="#7a0000">
          <Select style={{ width: 130 }} placeholder="Color">
            {COLOR_PRESETS.map(c => (
              <Select.Option key={c} value={c}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 14, height: 14, borderRadius: 3, background: c, display: 'inline-block' }} />
                  {c}
                </span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="sort_order">
          <InputNumber placeholder="Order" min={0} style={{ width: 70 }} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}
            style={{ background: '#7a0000', borderColor: '#7a0000' }}>
            {editId ? 'Update' : 'Add'}
          </Button>
          {editId && <Button style={{ marginLeft: 6 }} onClick={() => { setEditId(null); form.resetFields(); }}>Cancel</Button>}
        </Form.Item>
      </Form>
      <Divider />
      <Table
        dataSource={cats} rowKey="id" size="small" loading={loading} pagination={false}
        columns={[
          {
            title: 'Category', dataIndex: 'name',
            render: (n, r) => (
              <Tag style={{ border: `1px solid ${r.color}`, color: r.color, background: `${r.color}15`, fontWeight: 600 }}>
                {n}
              </Tag>
            ),
          },
          { title: 'Order', dataIndex: 'sort_order', width: 60 },
          {
            title: 'Status', dataIndex: 'is_active', width: 80,
            render: v => <Tag color={v ? 'green' : 'default'}>{v ? 'Active' : 'Off'}</Tag>,
          },
          {
            title: '', width: 80,
            render: (_, r) => (
              <Space size={4}>
                <Button size="small" icon={<EditOutlined />} onClick={() => startEdit(r)} />
                <Popconfirm title="Disable this category?" onConfirm={() => remove(r.id)}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
    </Modal>
  );
}

// ─── Main List ────────────────────────────────────────────────────────────────
export default function PortalUpdatesList() {
  const history = useHistory();
  const [data,       setData]       = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [catFilter,  setCatFilter]  = useState('');
  const [showCatMgr, setShowCatMgr] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const loadCats = useCallback(async () => {
    try {
      const r = await axios.get(`${baseUrl}/portal-categories`, { headers: authH() });
      if (r.data.status) setCategories(r.data.result);
    } catch {}
  }, []);

  const fetch = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: pagination.pageSize });
      if (search)    params.set('search', search);
      if (catFilter) params.set('category_id', catFilter);
      const res = await axios.get(`${baseUrl}/portal-updates?${params}`, { headers: authH() });
      if (res.data.status) {
        setData(res.data.result);
        setPagination(p => ({ ...p, current: pg, total: res.data.total }));
      }
    } catch { notification.error({ message: 'Load failed' }); }
    finally { setLoading(false); }
  }, [search, catFilter, pagination.pageSize]);

  useEffect(() => { loadCats(); }, [loadCats]);
  useEffect(() => { fetch(1); }, [search, catFilter]); // eslint-disable-line

  const remove = async (id) => {
    try {
      await axios.delete(`${baseUrl}/portal-updates/${id}`, { headers: authH() });
      notification.success({ message: 'Deleted' });
      fetch(pagination.current);
    } catch { notification.error({ message: 'Delete failed' }); }
  };

  const catMap = Object.fromEntries(categories.map(c => [c.id, c]));

  const columns = [
    { title: '#', dataIndex: 'id', width: 50 },
    {
      title: 'Title', dataIndex: 'title',
      render: (t, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{t}</div>
          {r.content && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{r.content.slice(0, 70)}{r.content.length > 70 ? '…' : ''}</div>}
        </div>
      ),
    },
    {
      title: 'Category', dataIndex: 'category_id', width: 170,
      render: (id, r) => {
        const cat = r.category_name ? { name: r.category_name, color: r.category_color } : catMap[id];
        if (!cat) return <span style={{ color: '#ccc' }}>—</span>;
        return (
          <Tag style={{ border: `1px solid ${cat.color}`, color: cat.color, background: `${cat.color}15`, fontWeight: 600 }}>
            {cat.name}
          </Tag>
        );
      },
    },
    {
      title: 'Date', dataIndex: 'post_date', width: 110,
      render: d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
    },
    {
      title: 'Image', dataIndex: 'image_url', width: 60,
      render: url => url
        ? <a href={url} target="_blank" rel="noopener noreferrer"><FileImageOutlined style={{ fontSize: 18, color: '#1890ff' }} /></a>
        : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      title: 'Published', dataIndex: 'is_published', width: 90,
      render: v => <Tag color={v ? 'green' : 'default'}>{v ? 'Published' : 'Draft'}</Tag>,
    },
    {
      title: 'Actions', width: 130,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => history.push(`/portal-updates/edit/${r.id}`)}>Edit</Button>
          <Popconfirm title="Delete this update?" onConfirm={() => remove(r.id)} okText="Yes" cancelText="No">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-content">
      <Card
        title={<span style={{ color: '#fff', fontWeight: 700 }}>Portal Updates — Community Feed</span>}
        headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
        extra={
          <Space>
            <Button icon={<SettingOutlined />} onClick={() => setShowCatMgr(true)}>Manage Categories</Button>
            <Button icon={<ReloadOutlined />} onClick={() => fetch(pagination.current)}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push('/portal-updates/add')}
              style={{ background: '#7a0000', borderColor: '#7a0000' }}>
              New Update
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
          <Search placeholder="Search updates..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: 240 }} allowClear />
          <Select placeholder="All Categories" value={catFilter || undefined}
            onChange={v => setCatFilter(v || '')} allowClear style={{ width: 200 }}>
            {categories.map(c => (
              <Select.Option key={c.id} value={String(c.id)}>
                <span style={{ color: c.color, fontWeight: 600 }}>{c.name}</span>
              </Select.Option>
            ))}
          </Select>
        </Space>

        <Table
          dataSource={data} columns={columns} rowKey="id" loading={loading}
          pagination={{
            current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
            onChange: (pg) => fetch(pg),
            showTotal: (t) => `Total ${t} updates`,
          }}
        />
      </Card>

      <CategoryManager
        open={showCatMgr}
        onClose={() => setShowCatMgr(false)}
        onChanged={loadCats}
      />
    </div>
  );
}
